import { createContext, type PropsWithChildren } from 'react';

import type { Cell } from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * Context for the subrow indicator.
 * Since it is not an individual column, we cannot use the cellDeps to invalidate
 * the memo of the rendered cell and we need to move the change through the
 * conditional context.
 * @internal
 */
export const SubrowCellContext = createContext<{
  subrowIndicatorColumn?: string;
  isExpanded?: boolean;
  canExpand?: boolean;
  canExpandDisabled?: boolean;
}>({});

/**
 * ContextProvider for the subrow indicator.
 * Since it is not an individual column, we cannot use the cellDeps to invalidate
 * the memo of the rendered cell and we need to move the change through the
 * conditional context.
 * @internal
 */
export function SubRowCellContextProvider<
  TData extends DataTableV2RowData,
  TValue,
>(
  props: PropsWithChildren<{
    isSubRowIndicatorColumn: boolean;
    cell: Cell<TData, TValue>;
  }>,
) {
  if (props.isSubRowIndicatorColumn) {
    return (
      <SubrowCellContext.Provider
        value={{
          isExpanded: props.cell.row.getIsExpanded(),
          canExpand: props.cell.row.getCanExpand(),
          canExpandDisabled: props.cell.row.getCanExpandDisabled(),
        }}
      >
        {props.children}
      </SubrowCellContext.Provider>
    );
  }
  return props.children;
}
